import { InputProps } from 'antd';
import { DateTime } from 'luxon';

type DateInputType = {
  day: string;
  month: string;
  year: string;
};

/** Returns null if input is invalid */
export const toISODate = (date: DateInputType): string | null => {
  if (!Number(date.day) || !Number(date.month) || !Number(date.year)) return null;

  const dateTime = DateTime.fromObject({
    day: Number(date.day),
    month: Number(date.month),
    year: Number(date.year)
  });

  if (!dateTime.isValid) return null;

  return dateTime.toISODate();
};

export const toDateInput = (date: string): DateInputType => {
  if (!date) return { day: '', month: '', year: '' };

  const dateTime = DateTime.fromISO(date);
  if (!dateTime.isValid) return { day: '', month: '', year: '' };

  const { day, month, year } = dateTime;

  return {
    day: String(day),
    month: String(month),
    year: String(year)
  };
};

export const getMonthStatus = (month: string): InputProps['status'] => {
  if (!month) return undefined;
  if (!Number(month)) return 'error';
  if (Number(month) > 12) return 'error';
  if (Number(month) < 1) return 'error';

  return undefined;
};

export const getYearStatus = (year: string): InputProps['status'] => {
  if (!year) return undefined;
  if (!Number(year)) return 'error';

  return undefined;
};

export const getDayStatus = (day: string, month: string, year: string): InputProps['status'] => {
  if (!day) return undefined;
  if (!Number(day)) return 'error';
  if (Number(day) > 31) return 'error';

  if (month && year && getMonthStatus(month) !== 'error' && getYearStatus(year) !== 'error') {
    const date = toISODate({ day, month, year });

    if (date?.toString() === 'Invalid Date') return 'error';
  }

  return undefined;
};

const formatSingleError = (field?: string) => `The ${field} must be a valid number.`;
const formatDoubleError = (field1?: string, field2?: string) => `The ${field1} and ${field2} must be valid numbers.`;
const tripleError = 'The day, month and year must be valid numbers.';
const example = 'For example 31 8 1984.';

export const getInvalidDateMessage = (
  dayStatus: InputProps['status'],
  monthStatus: InputProps['status'],
  yearStatus: InputProps['status']
) => {
  const invalidParts = [
    dayStatus === 'error' ? 'day' : undefined,
    monthStatus === 'error' ? 'month' : undefined,
    yearStatus === 'error' ? 'year' : undefined
  ].filter(Boolean);

  if (invalidParts.length === 1) return formatSingleError(invalidParts[0]);
  if (invalidParts.length === 2) return formatDoubleError(invalidParts[0], invalidParts[1]);
  if (invalidParts.length === 3) return tripleError;
  return example;
};

export const getTemplatedProps = (datePart: 'day' | 'month' | 'year', isDob: boolean): InputProps => ({
  autoComplete: isDob ? `bday-${datePart}` : 'off',
  'data-testid': `date-input-${datePart}`,
  name: datePart
});
